import { Award, Target, Users, Zap, CheckCircle2, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const values = [
  {
    icon: Target,
    title: "Fully Responsive",
    description: "Our websites are designed to be fully responsive, ensuring they look great and function perfectly on all devices.",
  },
  {
    icon: Users,
    title: "Clean Code",
    description: "We pride ourselves on writing clean code that is easy to read, understand, and maintain, ensuring your website runs smoothly.",
  },
  {
    icon: Zap,
    title: "Easy To Customize",
    description: "We understand the importance of flexibility, which is why our websites are built to be easily customized and adapted to your changing needs.",
  },
  {
    icon: Award,
    title: "SEO Best Practices",
    description: "With a focus on usability, accessibility, and SEO best practices, we help businesses establish a strong online presence.",
  },
];

const About = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="about" className="py-32 relative overflow-hidden bg-[#0B0C10]">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/5 to-background" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20 animate-fade-in">
          <div className="inline-block mb-4">
            <span className="text-sm font-semibold uppercase tracking-wider text-primary">About Us</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-foreground to-foreground/60 bg-clip-text text-transparent">
              Trionix Technologies is a
            </span>
            <br />
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Leading IT Solution Provider
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Transforming businesses through innovation, creativity, and cutting-edge technology
          </p>
        </div>

        {/* Main Content Card */}
        <div className="max-w-6xl mx-auto mb-20">
          <Card className="group relative bg-gradient-to-br from-card via-card to-card/50 border-border/50 backdrop-blur-sm hover:border-primary/30 transition-all duration-500 overflow-hidden">
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            
            <CardContent className="p-10 md:p-16 relative">
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div className="space-y-8">
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-1 w-12 bg-gradient-to-r from-primary to-secondary rounded-full" />
                      <span className="text-sm font-semibold uppercase tracking-wider text-primary">Our Story</span>
                    </div>
                    <h3 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                      Crafting Digital Excellence Since Day One
                    </h3>
                  </div>
                  
                  <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                    <p>
                      At Trionix, we understand that every business is unique, which is why we offer custom web design and development packages that are tailored to your specific needs.
                    </p>
                    <p className="text-foreground font-medium">
                      Our team of experienced designers and developers work closely with you to create websites that are not only visually stunning but also functional and user-friendly.
                    </p>
                  </div>

                  <Button
                    onClick={() => scrollToSection("contact")}
                    className="group/btn bg-gradient-to-r from-primary to-secondary hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 text-lg px-8 py-6 rounded-full"
                  >
                    Let's Work Together
                    <ArrowRight className="ml-2 h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {[
                    "Custom web design & development",
                    "E-commerce platforms & CMS builds",
                    "Usability & accessibility focused",
                    "SEO best practices implementation",
                    "Responsive across all devices"
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors duration-300"
                    >
                      <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <span className="text-foreground font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto mb-20">
          {values.map((value, index) => (
            <Card
              key={index}
              className="group relative bg-gradient-to-br from-card/80 via-card to-card/80 border-border/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-500 overflow-hidden"
            >
              {/* Animated Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/5 to-primary/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <CardContent className="p-8 text-center space-y-6 relative">
                <div className="flex justify-center">
                  <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                    <value.icon className="h-10 w-10 text-primary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-foreground">
                  {value.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center animate-fade-in">
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 blur-2xl" />
            <div className="relative p-12 bg-gradient-to-br from-card via-card to-card/80 border border-border/50 rounded-3xl backdrop-blur-sm">
              <h3 className="text-3xl md:text-4xl font-bold mb-4">
                Welcome to{" "}
                <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                  Trionix Technologies
                </span>
              </h3>
              <p className="text-xl text-muted-foreground mb-8">
                Let's go digital together
              </p>
              <Button
                onClick={() => scrollToSection("services")}
                variant="outline"
                className="border-2 border-primary/50 hover:border-primary hover:bg-primary/10 text-lg px-8 py-6 rounded-full font-semibold group"
              >
                Explore Our Services
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
